import React from 'react'
import { groupColor } from './theme'

/**
 * Tab bar for the nest results panel.
 *
 * Renders: Overview · [group tabs, colour-chipped] · All Parts · AI Coach · Versions · Export
 * Clicking a tab calls onChange(tabId). Current tab is visually emphasised.
 */
export default function NesterTabs({ colors, tabs, activeTab, onChange, badges = {} }) {
  return (
    <div style={styles.bar(colors)}>
      <div style={styles.inner}>
        {tabs.map((t, i) => {
          const active = activeTab === t.id
          const accent = t.color || colors.accent
          return (
            <button key={t.id}
              onClick={() => onChange(t.id)}
              title={t.title || t.label}
              style={{
                ...styles.tab(colors),
                ...(active ? styles.tabActive(colors, accent) : {}),
                borderBottomColor: active ? accent : 'transparent',
                color: active ? colors.text : colors.textMuted,
              }}>
              {t.dot && (
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: accent, flexShrink: 0,
                }} />
              )}
              <span>{t.label}</span>
              {t.count != null && (
                <span style={{
                  ...styles.count(colors),
                  background: active ? accent : colors.panelAlt,
                  color: active ? '#fff' : colors.textMuted,
                }}>{t.count}</span>
              )}
              {badges[t.id] && (
                <span style={{
                  ...styles.badge,
                  background: colors.warn,
                  color: '#fff',
                }}>{badges[t.id]}</span>
              )}
              {t.kbd && (
                <span style={styles.kbd(colors)}>{t.kbd}</span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Helper: derive the standard tab set from a nest result. */
export function buildNesterTabs(nestResult, materialGroups, options = {}) {
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      title: 'Project dashboard — everything at a glance',
      kbd: '1',
    },
  ]

  const groups = nestResult?.group_results || []
  if (groups.length > 0) {
    groups.forEach((g, i) => {
      const mg = materialGroups?.find(m => m.group_id === g.material_group_id)
      const label = mg?.group_name
        || g.material_group_name
        || [mg?.material_type, mg?.thickness_display].filter(Boolean).join(' ')
        || `Group ${i + 1}`
      tabs.push({
        id: `group-${g.material_group_id || `idx-${i}`}`,
        label,
        title: `Group ${i + 1} detail`,
        dot: true,
        color: groupColor(i),
        count: g.sheets_needed,
        kbd: i < 7 ? `${i + 2}` : null,
      })
    })
  } else if (nestResult) {
    // Single-group legacy result — treat as one "group" tab.
    tabs.push({
      id: 'group-single',
      label: 'Nest Detail',
      dot: true,
      color: groupColor(0),
      count: nestResult.sheets_needed,
      kbd: '2',
    })
  }

  tabs.push({
    id: 'parts',
    label: 'All Parts',
    title: 'Combined part list across every group',
    count: options.partsCount,
    kbd: '9',
  })

  if (options.showCoach) {
    tabs.push({
      id: 'coach',
      label: '🧠 AI Coach',
      title: 'Claude-powered optimisation advice',
    })
  }

  if (options.showVersions) {
    tabs.push({
      id: 'versions',
      label: 'Versions',
      title: 'Nest run history and diff',
    })
  }

  if (options.showRemnants) {
    tabs.push({
      id: 'remnants',
      label: '♻ Remnants',
      title: 'Candidate remnant sheets from prior jobs',
    })
  }

  tabs.push({
    id: 'exports',
    label: 'Exports',
    title: 'Download CSV, PDF, quote, shop packet',
  })

  return tabs
}

const styles = {
  bar: (c) => ({
    borderBottom: `1px solid ${c.border}`,
    background: c.panel,
    borderRadius: '8px 8px 0 0',
    padding: '0 6px',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
  }),
  inner: {
    display: 'flex', alignItems: 'stretch',
    gap: 2,
    minHeight: 40,
  },
  tab: (c) => ({
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '8px 14px',
    border: 'none', background: 'transparent',
    borderBottom: '2px solid transparent',
    fontSize: 13, fontWeight: 500,
    color: c.textMuted,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s, background 0.15s',
  }),
  tabActive: (c, accent) => ({
    background: c.panelAlt,
    fontWeight: 700,
  }),
  count: (c) => ({
    fontSize: 10, fontWeight: 700,
    padding: '1px 6px', borderRadius: 10,
    minWidth: 18, textAlign: 'center',
  }),
  badge: {
    fontSize: 10, fontWeight: 700,
    padding: '1px 6px', borderRadius: 10,
    lineHeight: 1.4,
  },
  kbd: (c) => ({
    fontSize: 10, fontWeight: 600,
    padding: '1px 5px', borderRadius: 3,
    border: `1px solid ${c.border}`,
    color: c.textFaint,
    marginLeft: 2,
    fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
  }),
}
